import React from 'react';
import { Alert, Box, Chip, LinearProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { DEEP_GREEN, MUTED } from '../colorTokens';
import { KIND_LABEL, VERDICT_COLOR, VERDICT_LABEL, formatDate, formatNumber, formatPercent } from './format';
import { useReadiness, useReadinessTrend } from './useReleaseCadence';
import { Headline, ScoreCard, TrendLine } from './ScoreParts';
import { NoRolloutNotice, VersionPicker, useVersionPicker } from './VersionPicker';
import PlanPrompt from './PlanPrompt';

const GROUP_LABEL = { internal: 'Internal', preprod: 'Pre-prod', standard: 'Standard', vip: 'VIP' };
const BAND_LABEL = { high: 'High confidence', medium: 'Medium confidence', low: 'Low confidence' };
const CHECK_COLUMNS = [
  { key: 'failureRate', label: 'Failure rate', format: (v) => formatPercent(v) },
  { key: 'unrecovered', label: 'Unrecovered', format: (v) => formatNumber(v) },
  { key: 'completion', label: 'Completion', format: (v) => formatPercent(v, 0) },
  { key: 'soak', label: 'Soak before next', format: (v) => `${v} d` }
];
const HEAD_SX = { bgcolor: '#F8F9FA', '& th': { color: DEEP_GREEN, fontWeight: 600, borderBottom: '2px solid rgba(26,46,34,0.15)' } };
const CARD_SX = { borderRadius: '12px', borderColor: 'rgba(0,0,0,0.05)' };

const VerdictChip = ({ verdict, label }) => (
  <Chip
    size="small"
    label={label || VERDICT_LABEL[verdict]}
    sx={{ height: 24, borderRadius: '8px', fontWeight: 600, color: '#fff', bgcolor: VERDICT_COLOR[verdict] }}
  />
);

const CheckCell = ({ wave, column }) => {
  const check = wave.checks.find((c) => c.key === column.key);
  const graded = check && check.status !== 'na';
  return (
    <TableCell align="right">
      <Tooltip title={check ? check.detail : ''} placement="top">
        <Box component="span" sx={{ color: graded ? VERDICT_COLOR[check.status] : MUTED, fontWeight: graded && check.status !== 'go' ? 700 : 400 }}>
          {check && check.value != null ? column.format(check.value) : '–'}
        </Box>
      </Tooltip>
    </TableCell>
  );
};

const trendTip = (p) => ({ title: p.version, kind: KIND_LABEL[p.kind], lines: [`${p.score} / 100`, `${BAND_LABEL[p.band]} · select to open`] });

const Summary = ({ data, entry, trend, onSelect }) => {
  const { score, verdict, signoffChecks, rollout, plan } = data;
  const delta = entry && entry.change != null ? { unit: 'score', change: entry.change, direction: entry.direction } : null;
  const since = entry && entry.previousVersion && `since ${entry.previousVersion} (${entry.previousScore}), the previous ${KIND_LABEL[entry.kind].toLowerCase()} release.`;
  return (
    <ScoreCard
      headline={<Headline caption={`Readiness score · ${data.version}`} value={String(score.value)} label={`out of 100 · ${BAND_LABEL[score.band]}`} measure={delta} since={since} />}
      chart={trend && trend.length > 0 ? <TrendLine title="Readiness score, release by release" legend="Filled dot: maintenance or hotfix · ring: feature release" data={trend} valueKey="score" unit="score" tip={trendTip} onSelect={onSelect} /> : null}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 6 }}>
        <Box>
          <Typography variant="caption" sx={{ color: MUTED }}>Sign-off verdict</Typography>
          <Box sx={{ mt: 0.5 }}><VerdictChip verdict={verdict} /></Box>
          <Box sx={{ mt: 0.75 }}>
            {signoffChecks.map((c) => (
              <Tooltip key={c.key} title={c.detail} placement="right">
                <Typography variant="body2" sx={{ color: MUTED }}>
                  <Box component="span" sx={{ color: VERDICT_COLOR[c.status], fontWeight: 700 }}>{VERDICT_LABEL[c.status]}</Box>
                  {' · '}{c.label}
                </Typography>
              </Tooltip>
            ))}
          </Box>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: MUTED }}>Rollout</Typography>
          <Typography sx={{ fontWeight: 600, color: DEEP_GREEN }}>{rollout.state === 'complete' ? 'Complete' : 'In progress'}</Typography>
          <Typography variant="body2" sx={{ color: MUTED }}>{rollout.reachedFinalWave ? 'VIP wave reached' : 'VIP wave not reached'}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: MUTED }}>Plan used</Typography>
          <Typography sx={{ fontWeight: 600, color: DEEP_GREEN }}>{plan ? plan.name : 'None'}</Typography>
          <Typography variant="body2" sx={{ color: MUTED }}>{plan ? 'Latest upload covering this version' : 'Upload one on the Plan vs actual tab'}</Typography>
        </Box>
      </Box>
    </ScoreCard>
  );
};

const ScoreBreakdown = ({ score }) => (
  <Paper variant="outlined" sx={{ ...CARD_SX, overflowX: 'auto' }}>
    <Typography sx={{ fontWeight: 600, color: DEEP_GREEN, p: 2, pb: 1 }}>How the score is built</Typography>
    <Table size="small">
      <TableHead>
        <TableRow sx={HEAD_SX}>
          <TableCell>Component</TableCell>
          <TableCell align="right">Weight</TableCell>
          <TableCell align="right">Value</TableCell>
          <TableCell sx={{ width: 220 }}>Points earned</TableCell>
          <TableCell>Basis</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {score.components.map((c) => (
          <TableRow key={c.key} sx={{ opacity: c.available ? 1 : 0.55 }}>
            <TableCell sx={{ fontWeight: 600 }}>{c.label}</TableCell>
            <TableCell align="right">{c.available ? `${c.weightShare}` : '–'}</TableCell>
            <TableCell align="right">{c.display == null ? '–' : c.key === 'failureRate' || c.key === 'planAdherence' ? formatPercent(c.display) : formatNumber(c.display)}</TableCell>
            <TableCell>
              {c.available ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress variant="determinate" value={c.fraction * 100} sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: 'rgba(0,0,0,0.06)', '& .MuiLinearProgress-bar': { bgcolor: DEEP_GREEN } }} />
                  <Typography variant="caption" sx={{ minWidth: 70, textAlign: 'right' }}>{c.points} of {c.weightShare}</Typography>
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: MUTED }}>Left out</Typography>
              )}
            </TableCell>
            <TableCell sx={{ color: MUTED }}>{c.detail}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
);

const WaveGates = ({ waves }) => (
  <Paper variant="outlined" sx={{ ...CARD_SX, overflowX: 'auto' }}>
    <Typography sx={{ fontWeight: 600, color: DEEP_GREEN, p: 2, pb: 0.5 }}>Wave gates</Typography>
    <Typography variant="body2" sx={{ color: MUTED, px: 2, pb: 1 }}>
      Each gate judges a wave by its own results and says whether the next wave should proceed. Hover a figure for the rule behind it.
    </Typography>
    <Table size="small">
      <TableHead>
        <TableRow sx={HEAD_SX}>
          <TableCell>Wave</TableCell>
          <TableCell>State</TableCell>
          <TableCell align="right">Instances</TableCell>
          {CHECK_COLUMNS.map((c) => <TableCell key={c.key} align="right">{c.label}</TableCell>)}
          <TableCell>Gate to next wave</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {waves.map((w) => (
          <TableRow key={w.group} hover>
            <TableCell sx={{ fontWeight: 600 }}>{GROUP_LABEL[w.group] || w.group}</TableCell>
            <TableCell>{w.state === 'complete' ? 'Complete' : 'In progress'}</TableCell>
            <TableCell align="right">{formatNumber(w.instances)}</TableCell>
            {CHECK_COLUMNS.map((c) => <CheckCell key={c.key} wave={w} column={c} />)}
            <TableCell>
              <VerdictChip verdict={w.verdict} label={w.nextGroup ? `${VERDICT_LABEL[w.verdict]} · ${GROUP_LABEL[w.nextGroup] || w.nextGroup}` : `${VERDICT_LABEL[w.verdict]} · final wave so far`} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
);

const StillFailing = ({ waves }) => {
  const withFailures = waves.filter((w) => w.stillFailing.total > 0);
  if (!withFailures.length) return null;
  return (
    <Paper variant="outlined" sx={{ ...CARD_SX, overflowX: 'auto' }}>
      <Typography sx={{ fontWeight: 600, color: DEEP_GREEN, p: 2, pb: 1 }}>Instances still failing</Typography>
      <Table size="small">
        <TableHead>
          <TableRow sx={HEAD_SX}>
            <TableCell>Wave</TableCell>
            <TableCell>Environment</TableCell>
            <TableCell>Instance</TableCell>
            <TableCell>Last status</TableCell>
            <TableCell align="right">Failed attempts</TableCell>
            <TableCell>Last attempt</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {withFailures.flatMap((w) => [
            ...w.stillFailing.instances.map((i) => (
              <TableRow key={`${w.group}|${i.env_name}|${i.instance}`} hover>
                <TableCell>{GROUP_LABEL[w.group] || w.group}</TableCell>
                <TableCell>{i.env_name}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{i.instance}</TableCell>
                <TableCell>{i.lastStatus}</TableCell>
                <TableCell align="right">{i.attempts}</TableCell>
                <TableCell>{formatDate(i.lastAt)}</TableCell>
              </TableRow>
            )),
            ...(w.stillFailing.total > w.stillFailing.instances.length
              ? [(
                <TableRow key={`${w.group}-more`}>
                  <TableCell colSpan={6} sx={{ color: MUTED }}>
                    {GROUP_LABEL[w.group] || w.group}: showing {w.stillFailing.instances.length} of {w.stillFailing.total} instances.
                  </TableCell>
                </TableRow>
              )]
              : [])
          ])}
        </TableBody>
      </Table>
    </Paper>
  );
};

const ReadinessView = ({ overview, onOpenPlans }) => {
  const { entries, entry, select, loading: catalogLoading, rolloutVersion } = useVersionPicker(overview, { skipSmall: true });
  const { data, loading, error } = useReadiness(rolloutVersion);
  const { data: trendData } = useReadinessTrend();
  const trend = trendData ? trendData.trend : null;
  const shown = rolloutVersion ? data : null;

  if (!entries.length) return <Typography sx={{ color: MUTED }}>No deployment runs are recorded yet.</Typography>;

  return (
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      <VersionPicker entries={entries} selected={entry ? entry.version : ''} onChange={select} loading={loading || catalogLoading} />
      {entry && !entry.hasRollout && <NoRolloutNotice entry={entry} historyStart={overview.history.earliest} onOpenPlans={onOpenPlans} />}
      {error && <Alert severity="error">{error}</Alert>}
      {shown && shown.verdict === 'insufficient' && <Alert severity="info">{shown.reason}</Alert>}
      {shown && shown.verdict !== 'insufficient' && (
        <>
          {shown.reason && <Alert severity="warning">{shown.reason}</Alert>}
          {!shown.plan && (
            <PlanPrompt onOpenPlans={onOpenPlans}>
              No deployment schedule covers {shown.version}, so plan completion and plan adherence are left out of the score. Upload the deployment schedule workbook to include them.
            </PlanPrompt>
          )}
          <Summary data={shown} entry={trend ? trend.find((t) => t.version === rolloutVersion) : null} trend={trend} onSelect={select} />
          {shown.waves.length > 0 && <WaveGates waves={shown.waves} />}
          <StillFailing waves={shown.waves} />
          <ScoreBreakdown score={shown.score} />
        </>
      )}
    </Box>
  );
};

export default ReadinessView;
